import React, { Component } from 'react';
import { compose } from 'redux';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import arrayMutators from 'final-form-arrays';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { ensureCurrentUser } from '../../../util/data';
import { getCountryOptions, SELLER_COUNTRY_CODES } from '../../../util/shipping';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import {
  isTooManyEmailVerificationRequestsError,
  isUploadImageOverLimitError,
} from '../../../util/errors';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';
import { PROFILE_SETTINGS_AVATAR_VARIANTS } from '../../../ducks/user.duck';

import {
  Form,
  Avatar,
  Button,
  ExternalLink,
  ImageFromFile,
  IconSpinner,
  FieldSelect,
  FieldTextInput,
  H4,
  CustomExtendedDataField,
  Modal,
} from '../../../components';

import EmailVerificationInfo from '../../AuthenticationPage/EmailVerificationInfo';

import ExpansionLeadModal from './ExpansionLeadModal';
import css from './ProfileSettingsForm.module.css';

const ACCEPT_IMAGES = 'image/*';
const UPLOAD_CHANGE_DELAY = 2000; // Show spinner so that browser has time to load img srcset
const SELLER_USER_TYPE = 'seller';
const WEARBY_SOCIAL_HANDLE = 'wearbymarket';
const WEARBY_SOCIAL_FOLLOW_HREFS = {
  instagram: `https://www.instagram.com/${WEARBY_SOCIAL_HANDLE}/`,
  x: `https://x.com/${WEARBY_SOCIAL_HANDLE}`,
  tiktok: `https://www.tiktok.com/@${WEARBY_SOCIAL_HANDLE}`,
};

const renderWearbyFollowLink = href => chunks => (
  <ExternalLink className={css.sellerApplicationPendingCta} href={href}>
    {chunks}
  </ExternalLink>
);

const SELLER_CATEGORY_FIELD_KEY = 'seller_category';
const SELLER_COUNTRY_FIELD_KEY = 'seller_country';
const SELLER_SOCIAL_FIELD_KEYS = ['instagram', 'x', 'tiktok'];
const HIDDEN_SOCIAL_FIELD_KEYS = ['onlyfans'];
const SELLER_APPLICATION_FIELD_KEYS = [
  SELLER_CATEGORY_FIELD_KEY,
  SELLER_COUNTRY_FIELD_KEY,
  ...SELLER_SOCIAL_FIELD_KEYS,
  ...HIDDEN_SOCIAL_FIELD_KEYS,
];

const getUserFieldKey = ({ key, name, fieldConfig }) =>
  fieldConfig?.key || key?.replace(/^pub_/, '') || name?.replace(/^pub_/, '');

const isSellerApplicationField = fieldProps =>
  SELLER_APPLICATION_FIELD_KEYS.includes(getUserFieldKey(fieldProps));

const isSellerCategoryField = fieldProps =>
  getUserFieldKey(fieldProps) === SELLER_CATEGORY_FIELD_KEY;

const isSellerCountryField = fieldProps => getUserFieldKey(fieldProps) === SELLER_COUNTRY_FIELD_KEY;

const isSocialField = fieldProps => SELLER_SOCIAL_FIELD_KEYS.includes(getUserFieldKey(fieldProps));

const isHiddenSocialField = fieldProps =>
  HIDDEN_SOCIAL_FIELD_KEYS.includes(getUserFieldKey(fieldProps));

const ensureSellerCategoryRequired = fieldProps => {
  if (!isSellerCategoryField(fieldProps)) {
    return fieldProps;
  }

  return {
    ...fieldProps,
    fieldConfig: {
      ...fieldProps.fieldConfig,
      saveConfig: {
        ...fieldProps.fieldConfig?.saveConfig,
        isRequired: true,
      },
    },
  };
};

const ensureSocialFieldsOptional = fieldProps => {
  if (!isSocialField(fieldProps)) {
    return fieldProps;
  }

  return {
    ...fieldProps,
    fieldConfig: {
      ...fieldProps.fieldConfig,
      saveConfig: {
        ...fieldProps.fieldConfig?.saveConfig,
        isRequired: false,
      },
    },
  };
};

const hasSellerSocialProfile = values =>
  SELLER_SOCIAL_FIELD_KEYS.some(key => {
    const value = values?.[`pub_${key}`];
    return typeof value === 'string' && value.trim().length > 0;
  });

/**
 * Social media inputs for seller application / seller profile, with requirement notes.
 *
 * @param {Object} props
 * @param {Array<Object>} props.fieldPropsList - Props for CustomExtendedDataField
 * @param {string} [props.formId] - Form id prefix
 * @param {string} [props.error] - Section-level validation message
 * @returns {JSX.Element|null}
 */
const SellerSocialFields = props => {
  const { fieldPropsList, formId, error } = props;
  if (!fieldPropsList?.length) {
    return null;
  }

  return (
    <div className={css.sellerSocialFields}>
      <div className={css.sellerSocialNotes}>
        <p className={css.sellerSocialRequiredNote}>
          <FormattedMessage id="ProfileSettingsForm.sellerSocialRequiredNote" />
        </p>
        <p className={css.sellerSocialPublicNote}>
          <FormattedMessage id="ProfileSettingsForm.sellerSocialPublicNote" />
        </p>
        {error ? (
          <p className={css.sellerSocialError} role="alert">
            {error}
          </p>
        ) : null}
      </div>
      {fieldPropsList.map(({ key, ...fieldProps }) => (
        <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
      ))}
    </div>
  );
};

const SellerApplicationPendingMessage = () => (
  <div className={css.sectionContainer}>
    <H4 as="h2" className={css.sectionTitle}>
      <FormattedMessage id="ProfileSettingsForm.sellerApplicationPendingHeading" />
    </H4>
    <div className={css.sellerApplicationPending}>
      <p className={css.sellerApplicationPendingText}>
        <FormattedMessage id="ProfileSettingsForm.sellerApplicationPending" />
      </p>
      <p className={css.sellerApplicationPendingText}>
        <FormattedMessage id="ProfileSettingsForm.sellerApplicationPendingInstagram" />
      </p>
      <p className={css.sellerApplicationPendingText}>
        <FormattedMessage
          id="ProfileSettingsForm.sellerApplicationPendingFollowCta"
          values={{
            handle: `@${WEARBY_SOCIAL_HANDLE}`,
            instagramLink: renderWearbyFollowLink(WEARBY_SOCIAL_FOLLOW_HREFS.instagram),
            xLink: renderWearbyFollowLink(WEARBY_SOCIAL_FOLLOW_HREFS.x),
            tiktokLink: renderWearbyFollowLink(WEARBY_SOCIAL_FOLLOW_HREFS.tiktok),
          }}
        />
      </p>
    </div>
  </div>
);

const ResendVerificationErrorMessage = props => {
  const { sendVerificationEmailError } = props;
  const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
    sendVerificationEmailError
  )
    ? 'AuthenticationPage.resendFailedTooManyRequests'
    : 'AuthenticationPage.resendFailed';

  return sendVerificationEmailError ? (
    <div className={css.error}>
      <FormattedMessage id={resendErrorTranslationId} />
    </div>
  ) : null;
};

const DisplayNameMaybe = props => {
  const { userTypeConfig, intl } = props;

  const isDisabled = userTypeConfig?.defaultUserFields?.displayName === false;
  if (isDisabled) {
    return null;
  }

  const { required } = userTypeConfig?.displayNameSettings || {};
  const isRequired = required === true;

  const validateMaybe = isRequired
    ? {
        validate: validators.required(
          intl.formatMessage({
            id: 'ProfileSettingsForm.displayNameRequired',
          })
        ),
      }
    : {};

  return (
    <div className={css.sectionContainer}>
      <FieldTextInput
        className={css.row}
        type="text"
        id="displayName"
        name="displayName"
        label={intl.formatMessage({
          id: 'ProfileSettingsForm.displayNameLabel',
        })}
        placeholder={intl.formatMessage({
          id: 'ProfileSettingsForm.displayNamePlaceholder',
        })}
        {...validateMaybe}
      />
      <p className={css.extraInfo}>
        <FormattedMessage id="ProfileSettingsForm.displayNameInfo" />
      </p>
    </div>
  );
};

/**
 * ProfileSettingsForm
 * TODO: change to functional component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.formId] - The form id
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {Object} props.userTypeConfig - The user type config
 * @param {string} props.userTypeConfig.userType - The user type
 * @param {Array<Object>} props.userFields - The user fields
 * @param {Object} [props.profileImage] - The profile image
 * @param {string} props.marketplaceName - The marketplace name
 * @param {Function} props.onImageUpload - The function to handle image upload
 * @param {Function} props.onSubmit - The function to handle form submission
 * @param {boolean} props.uploadInProgress - Whether the upload is in progress
 * @param {propTypes.error} [props.uploadImageError] - The upload image error
 * @param {boolean} props.updateInProgress - Whether the update is in progress
 * @param {propTypes.error} [props.updateProfileError] - The update profile error
 * @param {boolean} [props.ready] - Whether the profile was saved successfully
 * @param {Function} [props.onChange] - Called when the user edits the form after a successful save
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
class ProfileSettingsFormComponent extends Component {
  constructor(props) {
    super(props);

    this.uploadDelayTimeoutId = null;
    this.successClearTimeoutId = null;
    this.ignoreSuccessClear = false;
    this.state = {
      uploadDelay: false,
      sellerApplicationModalOpen:
        props.openSellerApplicationModal &&
        !props.isSeller &&
        !props.isSellerApplicationPending &&
        props.emailVerified !== false,
      expansionLeadModalOpen: false,
    };
    this.submittedValues = {};
  }

  componentDidUpdate(prevProps) {
    const shouldOpenSellerApplicationModal =
      this.props.openSellerApplicationModal &&
      !prevProps.openSellerApplicationModal &&
      !this.props.isSeller &&
      !this.props.isSellerApplicationPending &&
      this.props.emailVerified !== false;

    if (shouldOpenSellerApplicationModal) {
      this.setState({ sellerApplicationModalOpen: true });
    }

    if (!prevProps.ready && this.props.ready) {
      this.ignoreSuccessClear = true;
      window.clearTimeout(this.successClearTimeoutId);
      this.successClearTimeoutId = window.setTimeout(() => {
        this.ignoreSuccessClear = false;
      }, 500);
    }

    // Upload delay is additional time window where Avatar is added to the DOM,
    // but not yet visible (time to load image URL from srcset)
    if (prevProps.uploadInProgress && !this.props.uploadInProgress) {
      this.setState({ uploadDelay: true });
      this.uploadDelayTimeoutId = window.setTimeout(() => {
        this.setState({ uploadDelay: false });
      }, UPLOAD_CHANGE_DELAY);
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.uploadDelayTimeoutId);
    window.clearTimeout(this.successClearTimeoutId);
    if (typeof this.props.onSellerApplicationEmailVerificationNeeded === 'function') {
      this.props.onSellerApplicationEmailVerificationNeeded(false);
    }
  }

  render() {
    const { className, rootClassName, isSellerApplicationPending = false } = this.props;

    if (isSellerApplicationPending) {
      const classes = classNames(rootClassName || css.root, className);
      return (
        <div className={classes}>
          <SellerApplicationPendingMessage />
        </div>
      );
    }

    const { isSeller, intl, emailVerified = true } = this.props;
    const validate = values => {
      const isApplyingAsSeller = values?.sellerApplicationIntent === 'yes' && emailVerified;
      const requiresSocial = isSeller || isApplyingAsSeller;
      if (!requiresSocial || hasSellerSocialProfile(values)) {
        return {};
      }
      return {
        sellerSocialRequired: intl.formatMessage({
          id: 'ProfileSettingsForm.sellerSocialRequired',
        }),
      };
    };

    return (
      <FinalForm
        {...this.props}
        mutators={{ ...arrayMutators }}
        validate={validate}
        render={fieldRenderProps => {
          const {
            className,
            currentUser,
            handleSubmit,
            intl,
            invalid,
            errors = {},
            onImageUpload,
            pristine,
            profileImage,
            rootClassName,
            updateInProgress,
            updateProfileError,
            uploadImageError,
            uploadInProgress,
            form,
            formId,
            isSeller,
            isSellerApplicationPending = false,
            emailVerified = true,
            marketplaceName,
            onManageDisableScrolling = () => null,
            onChange,
            onResendVerificationEmail = () => null,
            onSellerApplicationEmailVerificationNeeded = () => null,
            ready = false,
            sendVerificationEmailError,
            sendVerificationEmailInProgress = false,
            values,
            userFields,
            userTypeConfig,
          } = fieldRenderProps;

          const user = ensureCurrentUser(currentUser);

          // First name
          const firstNameLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.firstNameLabel',
          });
          const firstNamePlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.firstNamePlaceholder',
          });
          const firstNameRequiredMessage = intl.formatMessage({
            id: 'ProfileSettingsForm.firstNameRequired',
          });
          const firstNameRequired = validators.required(firstNameRequiredMessage);

          // Last name
          const lastNameLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.lastNameLabel',
          });
          const lastNamePlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.lastNamePlaceholder',
          });
          const lastNameRequiredMessage = intl.formatMessage({
            id: 'ProfileSettingsForm.lastNameRequired',
          });
          const lastNameRequired = validators.required(lastNameRequiredMessage);

          // Bio
          const bioLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.bioLabel',
          });
          const bioPlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.bioPlaceholder',
          });
          const sellerApplicationLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.sellerApplicationLabel',
          });
          const sellerApplicationPlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.sellerApplicationPlaceholder',
          });
          const sellerCountryLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.sellerCountryLabel',
          });
          const sellerCountryPlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.sellerCountryPlaceholder',
          });
          const sellerCountryRequired = validators.required(
            intl.formatMessage({
              id: 'ProfileSettingsForm.sellerCountryRequired',
            })
          );
          const sellerCountryOptions = getCountryOptions(intl.locale, SELLER_COUNTRY_CODES);

          const uploadingOverlay =
            uploadInProgress || this.state.uploadDelay ? (
              <div className={css.uploadingImageOverlay}>
                <IconSpinner />
              </div>
            ) : null;

          const hasUploadError = !!uploadImageError && !uploadInProgress;
          const errorClasses = classNames({ [css.avatarUploadError]: hasUploadError });
          const transientUserProfileImage = profileImage.uploadedImage || user.profileImage;
          const transientUser = { ...user, profileImage: transientUserProfileImage };

          // Ensure that file exists if imageFromFile is used
          const fileExists = !!profileImage.file;
          const fileUploadInProgress = uploadInProgress && fileExists;
          const delayAfterUpload = profileImage.imageId && this.state.uploadDelay;
          const imageFromFile =
            fileExists && (fileUploadInProgress || delayAfterUpload) ? (
              <ImageFromFile
                id={profileImage.id}
                className={errorClasses}
                rootClassName={css.uploadingImage}
                aspectWidth={1}
                aspectHeight={1}
                file={profileImage.file}
              >
                {uploadingOverlay}
              </ImageFromFile>
            ) : null;

          // Avatar is rendered in hidden during the upload delay
          // Upload delay smoothes image change process:
          // responsive img has time to load srcset stuff before it is shown to user.
          const avatarClasses = classNames(errorClasses, css.avatar, {
            [css.avatarInvisible]: this.state.uploadDelay,
          });
          const avatarComponent =
            !fileUploadInProgress && profileImage.imageId ? (
              <Avatar
                className={avatarClasses}
                renderSizes="96px"
                imageVariants={PROFILE_SETTINGS_AVATAR_VARIANTS}
                user={transientUser}
                disableProfileLink
              />
            ) : null;

          const submitError = updateProfileError ? (
            <div className={css.error}>
              <FormattedMessage id="ProfileSettingsForm.updateProfileFailed" />
            </div>
          ) : null;

          const classes = classNames(rootClassName || css.root, className);
          const submitInProgress = updateInProgress;
          const submittedOnce = Object.keys(this.submittedValues).length > 0;
          const pristineSinceLastSubmit = submittedOnce && isEqual(values, this.submittedValues);
          const submitDisabled =
            invalid ||
            pristine ||
            pristineSinceLastSubmit ||
            ready ||
            uploadInProgress ||
            submitInProgress;

          const fieldsUserType =
            isSeller || isSellerApplicationPending ? SELLER_USER_TYPE : userTypeConfig?.userType;
          const userFieldProps = getPropsForCustomUserFieldInputs(
            userFields,
            fieldsUserType,
            false
          ).filter(
            fieldProps =>
              !isSellerCountryField(fieldProps) &&
              !isHiddenSocialField(fieldProps) &&
              !isSocialField(fieldProps) &&
              (isSeller || isSellerApplicationPending || !isSellerApplicationField(fieldProps))
          );
          const sellerApplicationFieldProps = getPropsForCustomUserFieldInputs(
            userFields,
            SELLER_USER_TYPE,
            false
          )
            .filter(isSellerApplicationField)
            .filter(fieldProps => !isSellerCountryField(fieldProps))
            .filter(fieldProps => !isSocialField(fieldProps))
            .filter(fieldProps => !isHiddenSocialField(fieldProps))
            .map(ensureSellerCategoryRequired);
          const sellerSocialFieldProps = getPropsForCustomUserFieldInputs(
            userFields,
            SELLER_USER_TYPE,
            false
          )
            .filter(isSocialField)
            .map(ensureSocialFieldsOptional);
          const sellerSocialError = errors.sellerSocialRequired || null;
          const showSellerApplicationFields =
            !isSeller && !isSellerApplicationPending && values?.sellerApplicationIntent === 'yes';
          const showSellerApplicationSection = !isSeller && !isSellerApplicationPending;
          const showSellerApplicationEmailVerification =
            showSellerApplicationFields && !emailVerified;
          const showSellerApplicationFieldsVerified = showSellerApplicationFields && emailVerified;

          const closeSellerApplicationModal = () => {
            this.setState({ sellerApplicationModalOpen: false });
          };
          const chooseSellerApplicationIntent = intent => {
            form.change('sellerApplicationIntent', intent);
            form.blur('sellerApplicationIntent');
            closeSellerApplicationModal();
          };
          const userFirstName = user?.attributes?.profile?.firstName;
          const userEmail = user?.attributes?.email;
          const sellerApplicationModal = showSellerApplicationSection ? (
            <Modal
              id="ProfileSettingsForm.sellerApplicationModal"
              isOpen={this.state.sellerApplicationModalOpen}
              onClose={closeSellerApplicationModal}
              onManageDisableScrolling={onManageDisableScrolling}
              usePortal
            >
              <div className={css.sellerApplicationModalContent}>
                <H4 as="h2" className={css.sellerApplicationModalTitle}>
                  <FormattedMessage id="ProfileSettingsForm.sellerApplicationModalTitle" />
                </H4>
                <div className={css.sellerApplicationModalActions}>
                  <Button
                    className={css.sellerApplicationModalYesButton}
                    type="button"
                    onClick={() => chooseSellerApplicationIntent('yes')}
                  >
                    <FormattedMessage id="ProfileSettingsForm.sellerApplicationModalYes" />
                  </Button>
                  <Button
                    className={css.sellerApplicationModalNoButton}
                    type="button"
                    onClick={() => chooseSellerApplicationIntent('no')}
                  >
                    <FormattedMessage id="ProfileSettingsForm.sellerApplicationModalNo" />
                  </Button>
                </div>
              </div>
            </Modal>
          ) : null;

          const expansionLeadModal = showSellerApplicationFieldsVerified ? (
            <ExpansionLeadModal
              isOpen={this.state.expansionLeadModalOpen}
              onClose={() => this.setState({ expansionLeadModalOpen: false })}
              onManageDisableScrolling={onManageDisableScrolling}
              initialEmail={userEmail || ''}
            />
          ) : null;

          return (
            <>
              <Form
                className={classes}
                onSubmit={e => {
                  this.submittedValues = values;
                  handleSubmit(e);
                }}
              >
                {sellerApplicationModal}
                <FormSpy
                  subscription={{ pristine: true }}
                  onChange={({ pristine }) => {
                    if (!this.ignoreSuccessClear && !pristine && ready && onChange) {
                      onChange();
                    }
                  }}
                />
                <div className={css.sectionContainer}>
                  <Field
                    accept={ACCEPT_IMAGES}
                    id="profileImage"
                    name="profileImage"
                    type="file"
                    form={null}
                    uploadImageError={uploadImageError}
                    disabled={uploadInProgress}
                  >
                    {fieldProps => {
                      const { accept, id, input, disabled, uploadImageError } = fieldProps;
                      const { name, type } = input;
                      const onChange = e => {
                        const file = e.target.files[0];
                        form.change(`profileImage`, file);
                        form.blur(`profileImage`);
                        if (file != null) {
                          const tempId = `${file.name}_${Date.now()}`;
                          onImageUpload({ id: tempId, file });
                        }
                      };

                      let error = null;

                      if (isUploadImageOverLimitError(uploadImageError)) {
                        error = (
                          <div className={css.error}>
                            <FormattedMessage id="ProfileSettingsForm.imageUploadFailedFileTooLarge" />
                          </div>
                        );
                      } else if (uploadImageError) {
                        error = (
                          <div className={css.error}>
                            <FormattedMessage id="ProfileSettingsForm.imageUploadFailed" />
                          </div>
                        );
                      }

                      return (
                        <div className={css.uploadAvatarWrapper}>
                          <input
                            accept={accept}
                            id={id}
                            name={name}
                            className={css.uploadAvatarInput}
                            disabled={disabled}
                            onChange={onChange}
                            type={type}
                          />
                          <div className={css.photoRow}>
                            <div
                              className={classNames(css.photoImageContainer, {
                                [css.avatarUploadError]: hasUploadError,
                              })}
                            >
                              {imageFromFile}
                              {avatarComponent}
                              {!imageFromFile && !avatarComponent && (
                                <div className={css.avatarPlaceholder} />
                              )}
                            </div>
                            <div className={css.photoDescription}>
                              <p className={css.tip}>
                                <FormattedMessage id="ProfileSettingsForm.tip" />
                              </p>
                              <p className={css.fileInfo}>
                                <FormattedMessage id="ProfileSettingsForm.fileInfo" />
                              </p>
                            </div>
                            <label className={css.changePhotoButton} htmlFor={id}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                <circle cx="12" cy="13" r="4" />
                              </svg>
                              <FormattedMessage id="ProfileSettingsForm.changeAvatar" />
                            </label>
                          </div>
                          {error}
                        </div>
                      );
                    }}
                  </Field>
                </div>
                <div className={css.sectionContainer}>
                  <div className={css.nameContainer}>
                    <FieldTextInput
                      className={css.firstName}
                      type="text"
                      id="firstName"
                      name="firstName"
                      label={firstNameLabel}
                      placeholder={firstNamePlaceholder}
                      validate={firstNameRequired}
                    />
                    <FieldTextInput
                      className={css.lastName}
                      type="text"
                      id="lastName"
                      name="lastName"
                      label={lastNameLabel}
                      placeholder={lastNamePlaceholder}
                      validate={lastNameRequired}
                    />
                  </div>
                </div>

                <DisplayNameMaybe userTypeConfig={userTypeConfig} intl={intl} />

                <div className={css.sectionContainer}>
                  <H4 as="h2" className={css.sectionTitle}>
                    <FormattedMessage id="ProfileSettingsForm.bioHeading" />
                  </H4>
                  <FieldTextInput
                    type="textarea"
                    id="bio"
                    name="bio"
                    label={bioLabel}
                    placeholder={bioPlaceholder}
                  />
                  <p className={css.extraInfo}>
                    <FormattedMessage
                      id="ProfileSettingsForm.bioInfo"
                      values={{ marketplaceName }}
                    />
                  </p>
                </div>

                {showSellerApplicationSection ? (
                  <FormSpy
                    subscription={{ values: true }}
                    onChange={({ values: formValues }) => {
                      const needsVerification =
                        formValues?.sellerApplicationIntent === 'yes' && !emailVerified;
                      // Defer so we don't update ProfileSettingsPage while Final Form is rendering.
                      window.setTimeout(() => {
                        onSellerApplicationEmailVerificationNeeded(needsVerification);
                      }, 0);
                    }}
                  />
                ) : null}

                {showSellerApplicationSection ? (
                  <div className={css.sectionContainer}>
                    <H4 as="h2" className={css.sectionTitle}>
                      <FormattedMessage id="ProfileSettingsForm.sellerApplicationHeading" />
                    </H4>
                    <FieldSelect
                      className={css.row}
                      id="sellerApplicationIntent"
                      name="sellerApplicationIntent"
                      label={sellerApplicationLabel}
                    >
                      <option value="">{sellerApplicationPlaceholder}</option>
                      <option value="yes">
                        {intl.formatMessage({ id: 'ProfileSettingsForm.sellerApplicationYes' })}
                      </option>
                      <option value="no">
                        {intl.formatMessage({ id: 'ProfileSettingsForm.sellerApplicationNo' })}
                      </option>
                    </FieldSelect>
                    {showSellerApplicationEmailVerification ? (
                      <div className={css.sellerApplicationFields}>
                        <EmailVerificationInfo
                          name={userFirstName}
                          email={userEmail}
                          showCloseLink={false}
                          onResendVerificationEmail={onResendVerificationEmail}
                          resendErrorMessage={
                            <ResendVerificationErrorMessage
                              sendVerificationEmailError={sendVerificationEmailError}
                            />
                          }
                          sendVerificationEmailInProgress={sendVerificationEmailInProgress}
                        />
                      </div>
                    ) : null}
                    {showSellerApplicationFieldsVerified ? (
                      <div className={css.sellerApplicationFields}>
                        <div className={css.sellerApplicationCountry}>
                          <FieldSelect
                            className={css.row}
                            id={`${formId}.pub_${SELLER_COUNTRY_FIELD_KEY}`}
                            name={`pub_${SELLER_COUNTRY_FIELD_KEY}`}
                            label={sellerCountryLabel}
                            validate={sellerCountryRequired}
                          >
                            <option disabled value="">
                              {sellerCountryPlaceholder}
                            </option>
                            {sellerCountryOptions.map(country => (
                              <option key={country.code} value={country.code}>
                                {country.name}
                              </option>
                            ))}
                          </FieldSelect>
                          <button
                            type="button"
                            className={css.unsupportedCountryLink}
                            onClick={() => this.setState({ expansionLeadModalOpen: true })}
                          >
                            <FormattedMessage id="ProfileSettingsForm.unsupportedCountryLink" />
                          </button>
                        </div>
                        {sellerApplicationFieldProps.map(({ key, ...fieldProps }) => (
                          <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
                        ))}
                        <SellerSocialFields
                          fieldPropsList={sellerSocialFieldProps}
                          formId={formId}
                          error={sellerSocialError}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {isSeller && sellerSocialFieldProps.length ? (
                  <div className={css.sectionContainer}>
                    <SellerSocialFields
                      fieldPropsList={sellerSocialFieldProps}
                      formId={formId}
                      error={sellerSocialError}
                    />
                  </div>
                ) : null}

                <div className={classNames(css.sectionContainer, css.lastSection)}>
                  {userFieldProps.map(({ key, ...fieldProps }) => (
                    <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
                  ))}
                </div>
                {submitError}
                <Button
                  className={css.submitButton}
                  type="submit"
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                  ready={ready}
                >
                  <svg
                    style={{ fill: 'transparent' }}
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.29833 3.125H6.04167C5.26812 3.125 4.52625 3.43229 3.97927 3.97927C3.43229 4.52625 3.125 5.26812 3.125 6.04167V14.2308C3.125 14.9321 3.40358 15.6047 3.89946 16.1005C4.39534 16.5964 5.06789 16.875 5.76917 16.875M6.29833 3.125V5.16C6.29833 5.60203 6.47393 6.02595 6.78649 6.33851C7.09905 6.65107 7.52297 6.82667 7.965 6.82667H9.92C10.362 6.82667 10.786 6.65107 11.0985 6.33851C11.4111 6.02595 11.5867 5.60203 11.5867 5.16V3.125M6.29833 3.125H11.5867M5.76917 16.875V12.5C5.76917 11.837 6.03256 11.2011 6.5014 10.7322C6.97024 10.2634 7.60613 10 8.26917 10H11.7308C12.3939 10 13.0298 10.2634 13.4986 10.7322C13.9674 11.2011 14.2308 11.837 14.2308 12.5V16.875M5.76917 16.875H14.2308M11.5867 3.125H11.6083C11.9368 3.12496 12.262 3.18963 12.5654 3.31532C12.8688 3.441 13.1445 3.62524 13.3767 3.8575L16.0208 6.50167C16.2917 6.77253 16.5065 7.09408 16.6531 7.44796C16.7996 7.80185 16.875 8.18113 16.875 8.56417V14.2308C16.875 14.9321 16.5964 15.6047 16.1005 16.1005C15.6047 16.5964 14.9321 16.875 14.2308 16.875"
                      stroke="white"
                      stroke-width="1.25"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  <FormattedMessage id="ProfileSettingsForm.saveChanges" />
                </Button>
              </Form>
              {expansionLeadModal}
            </>
          );
        }}
      />
    );
  }
}

const ProfileSettingsForm = compose(injectIntl)(ProfileSettingsFormComponent);

ProfileSettingsForm.displayName = 'ProfileSettingsForm';

export default ProfileSettingsForm;
